import type { InventoryItem, UpdateProductBody } from 'core/types';
import {
  DEFAULT_MANUFACTURER_ID,
  NA_MANUFACTURER_ID,
  SelectedCatalogModel
} from 'features/shared/inventory/hooks/useProductCatalogEditFields';

/**
 * Builds the PATCH body for an item-detail Save, given the current edit
 * fields — pulled out of InventoryDetailsDesktop.tsx's saveEdits() and
 * InventoryProductEditForm.tsx's save(), which carried byte-identical copies
 * of this precedence logic (Model # wins over Brand/Manufacturer if both are
 * set; only the delta from `product` is sent).
 */
export const buildProductEditPatchBody = (
  product: InventoryItem,
  serial: string,
  model: SelectedCatalogModel | null,
  manufacturerId: number | null
): UpdateProductBody => {
  const body: UpdateProductBody = {};
  if (serial !== (product.serial || '')) {
    body.serial = serial || null;
  }
  if (model && model.id !== product.product_template.id) {
    body.product_template_id = model.id;
  } else if (
    !model &&
    manufacturerId === NA_MANUFACTURER_ID &&
    product.product_template.manufacturer?.id !== DEFAULT_MANUFACTURER_ID
  ) {
    // "N/A" selected — the API special-cases DEFAULT_MANUFACTURER_ID to
    // resolve via the one fully-generic template per Product Type, not a
    // per-brand row (never send the UI-only NA_MANUFACTURER_ID sentinel).
    body.manufacturer_id = DEFAULT_MANUFACTURER_ID;
  } else if (
    !model &&
    manufacturerId &&
    manufacturerId !== NA_MANUFACTURER_ID &&
    manufacturerId !== (product.product_template.manufacturer?.id ?? null)
  ) {
    // Brand picked with no Model # completed — the API resolves this to a
    // found-or-created "Default/Generic" model template for this brand+type.
    body.manufacturer_id = manufacturerId;
  }
  return body;
};
